<script setup lang="ts">
import { computed, onActivated, Ref, ref, toRefs, watch, withDefaults } from 'vue';
import { useDisplay } from 'vuetify';
import {
  SelectedContentsPanel,
  SelectionBottomSheet,
  SelectModeButton,
  ContentTypes,
  type SelectionAction,
} from '@ilteducation/components';
import { useLocale } from '@/plugins/translation';
import {
  ContentFilters,
  ContentReference,
  useContentCollection,
  parseReference,
  stringifyReference,
} from '@ilteducation/content-collections';
import { useSearch } from '@ilteducation/content-search';
import { useActivityStream } from '@/composables/use-activity-stream';
import { storeToRefs } from 'pinia';
import { VTextField } from 'vuetify/components/VTextField';
import type { Content, VideoV1 } from '@ilteducation/content-registry-types';
import { useVideos } from '@ilteducation/videos';
import ContentList from '@/components/collections/ContentList.vue';
import type { ContentWithStringReference } from '@/components/collections/ContentList.vue';
import { useUserContext } from '@ilteducation/ilt-auth';
import debounce from 'lodash/debounce';
import { useRoute } from 'vue-router';

type Props = {
  contentCollectionId: string;
  filters?: ContentFilters;
  contentType?: ContentTypes;
};
const props = withDefaults(defineProps<Props>(), {
  contentType: ContentTypes.Video,
});

const { contentCollectionId, contentType } = toRefs(props);

const emit = defineEmits<{
  (e: 'onAddContent'): void;
}>();

const route = useRoute();
const { t } = useLocale();
const { smAndDown } = useDisplay();
const { collection } = useContentCollection(contentCollectionId);
const { logAddContentToCollection } = useActivityStream();

// Create separate stores for Video, Article, and Material content types
const videoSearchStore = useSearch<Content>(ContentTypes.Video);
const articleSearchStore = useSearch<Content>(ContentTypes.Article);
const materialSearchStore = useSearch<Content>(ContentTypes.Material);
const { next: videoNext } = videoSearchStore;
const { next: articleNext } = articleSearchStore;
const { next: materialNext } = materialSearchStore;
const { result: videoResult, isLoading: videoIsLoading } = storeToRefs(videoSearchStore);
const { result: articleResult, isLoading: articleIsLoading } = storeToRefs(articleSearchStore);
const { result: materialResult, isLoading: materialIsLoading } = storeToRefs(materialSearchStore);

const { addVideos } = useVideos();

// Current active store results based on contentType
const result = computed(() => {
  if (contentType.value === ContentTypes.Article) return articleResult.value;
  if (contentType.value === ContentTypes.Material) return materialResult.value;
  return videoResult.value;
});

const isLoading = computed(() => {
  if (contentType.value === ContentTypes.Article) return articleIsLoading.value;
  if (contentType.value === ContentTypes.Material) return materialIsLoading.value;
  return videoIsLoading.value;
});

const { addContents } = useContentCollection(contentCollectionId);
const userContext = useUserContext();
const { realm } = storeToRefs(userContext);

const availableContent = ref<ContentWithStringReference[]>([]);

const searchInput = ref<string>('');
const isProcessing = ref<boolean>(false);
const selectedContents = ref<string[]>([]);
const showSelects = ref<boolean>(false);
const contentList = ref();
const searchInputElementRef = ref<VTextField>();

const getAddedContent: Ref<string[]> = computed(() => {
  if (collection.value?.contents.type === 'ContentsByReferences') {
    return collection.value.contents.references.map((c) => stringifyReference(c));
  }
  return [];
});

const onAddContent = async (content?: string[]) => {
  isProcessing.value = true;
  const contentToAdd = content || selectedContents.value;

  await addContents(contentToAdd.map((c) => parseReference(c)));
  if (contentToAdd.length > 0) {
    logAddContentToCollection(route.fullPath);
  }
  isProcessing.value = false;
  selectedContents.value = [];
  emit('onAddContent');
};

const panelActions = computed<SelectionAction[]>(() => [
  { key: 'add', label: t('components.add_button') as string, icon: 'fa-solid fa-plus', onSelect: onAddContent },
]);

const onSearch = (input: string) => {
  if (contentType.value === ContentTypes.Article) {
    articleSearchStore.search(input || '', {}, 20, [{ field: 'lastUpdatedAt', order: 'desc' }], 'and');
  } else if (contentType.value === ContentTypes.Material) {
    materialSearchStore.search(input || '', { language: 'sv' }, 20, [{ field: 'lastUpdatedAt', order: 'desc' }]);
  } else {
    videoSearchStore.search(input, {}, 20, [{ field: 'lastUpdatedAt', order: 'desc' }]);
  }
};

const debounceSearch = debounce((input: string) => {
  onSearch(input);
}, 300);

// Watch for content type changes and reset search state
watch(
  contentType,
  (newContentType, oldContentType) => {
    // Clear search results and reset state when content type changes
    if (oldContentType !== undefined && newContentType !== oldContentType) {
      selectedContents.value = [];
      searchInput.value = '';
      // Trigger a new search with the new content type
      onSearch('');
    }
  },
  { immediate: true },
);

const populateAvailableContent = (currentResult: Content[]) => {
  if (contentType.value === ContentTypes.Video) {
    addVideos(currentResult);
  }

  availableContent.value = currentResult.flatMap((content: Content) => {
    const reference: ContentReference = {
      id: content.id,
      type: contentType.value,
    };

    // Handle special case for videos with multiple subjects
    if (content.type === 'VideoV1' && contentType.value === ContentTypes.Video) {
      const video = content as VideoV1;
      const marketVideos = video.videos?.filter((v) => v.market === realm.value);
      if (marketVideos && marketVideos.length > 1) {
        // adding mode: if a video has multiple subjects, create multiple entries for each subject
        return marketVideos.map((v) => {
          return { ...video, stringReference: stringifyReference({ ...reference, categoryId: v.categoryId }) };
        });
      }
    }

    // Default case for all content types
    return { ...content, stringReference: stringifyReference(reference) };
  });
};

watch(result, async (newResult) => {
  if (newResult && newResult.length > 0) {
    populateAvailableContent(newResult);
  } else {
    // Handle empty results
    availableContent.value = [];
  }
});

onActivated(() => {
  onSearch('');
  // If there are already results in the store, populate availableContent immediately
  const currentResult = result.value;
  if (currentResult && currentResult.length > 0) {
    populateAvailableContent(currentResult);
  }
});

const onToggleSelect = () => {
  showSelects.value = !showSelects.value;
};

const loseSearchInputFocus = () => {
  if (searchInputElementRef.value) {
    searchInputElementRef.value.blur();
  }
};
</script>

<template>
  <div>
    <div class="d-flex ma-0 mb-4 align-center justify-space-between ga-4">
      <div class="flex-grow-1 d-flex align-center flex-wrap ga-3">
        <v-text-field
          id="add-content-search-input"
          ref="searchInputElementRef"
          v-model="searchInput"
          type="search"
          :label="t('components.search')"
          single-line
          density="comfortable"
          rounded="default"
          variant="solo"
          hide-details
          class="search-input pa-1"
          :clearable="true"
          color="tertiary-fixed-dim"
          :aria-label="t('components.search')"
          aria-describedby="search-input"
          @update:modelValue="debounceSearch"
          @click:clear="searchInput = ''"
          @keyup.enter="loseSearchInputFocus"
        >
          <template #append-inner>
            <v-icon size="xs" icon="fa:far fa-magnifying-glass"></v-icon>
          </template>
        </v-text-field>
        <SelectedContentsPanel
          v-if="showSelects && !smAndDown"
          rounded="default"
          :selected-content-total="selectedContents.length"
          :actions="panelActions"
          :show-selected="true"
          :is-disabled="selectedContents.length === 0"
        />
      </div>
      <SelectModeButton
        :model-value="showSelects"
        rounded="default"
        variant="text"
        color="primary"
        :bordered="false"
        @update:model-value="onToggleSelect"
      />
    </div>
    <SelectionBottomSheet :model-value="showSelects">
      <SelectedContentsPanel
        :card="false"
        :selected-content-total="selectedContents.length"
        :actions="panelActions"
        :show-selected="true"
        :is-disabled="selectedContents.length === 0"
      />
    </SelectionBottomSheet>
    <div ref="contentList" class="content-wrap">
      <ContentList
        v-if="contentList"
        v-model="selectedContents"
        :showHeader="false"
        :rounded="true"
        :content-items="availableContent"
        :disabled-to-add-content="getAddedContent"
        :is-loading="isLoading || isProcessing"
        :is-managing="showSelects"
        :is-adding="true"
        :parent-scroll-element="contentList"
        @on-load-content="
          () => {
            if (contentType === ContentTypes.Article) {
              articleNext();
            } else if (contentType === ContentTypes.Material) {
              materialNext();
            } else {
              videoNext();
            }
          }
        "
        @search="onSearch"
        @add-content="onAddContent"
      >
      </ContentList>
    </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.content-wrap a) {
  color: inherit !important;
}

.content-wrap {
  overflow-y: auto;
  height: calc(100vh - 180px);
}

:deep(.v-field) {
  border: 1px solid rgb(var(--v-theme-primary));
  box-shadow: none;
}
:deep(.v-field--focused) {
  border: 2px solid rgb(var(--v-theme-primary));
  margin: -1px;
}
:deep(input) {
  outline: none !important;
}
</style>
